import { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';
import Discipline from "../../api/models/discipline";
import { getDisciplines } from '../../api/services/meService';
import Modal from '../modal';
import { useAppSelector } from '../../app/hooks';
import { useDispatch } from 'react-redux';
import { updateDisciplineId, updateDiscipline } from '../../slices/userSlice';
import { sortAlphabetically } from '../../common/utils';
import DisciplinesResponse from '../../api/responses/disciplinesResponse';


type Props = {
  open?: boolean; //the visibility state of the popup
  toggle: (open: boolean) => void; //when the visiblity should change
  disciplineId: number; //the selected discipline -1 for none
  noInitialChange?: boolean;
  setDiscipline: (selectedDiscipline: Discipline) => void; //when the dicipline has changed.
  repressAlert?: boolean; //Represses the no disciplines alert
};

//catching the disciplines to reduce API calls
const getDisciplinesCached = async () => {
  //TODO build caching logic to prevent this API call being called multiple times per page load.
  //  The cache needs to be flushed when the user token is changed (IE logs on, refresh token occurs, impersonation occurs)
  //  We should also flush the cache after 1h, just incase it chagnes from admins
  //  we can use local storage for this.
  let cache = localStorage.getItem('cachedDisciplines');
  let cacheTimeString = localStorage.getItem('cachedDisciplinesTime');
  let cacheTime = undefined;
  let now = new Date().getTime();

  if (cacheTimeString) {
    cacheTime = parseInt(cacheTimeString);
  }

  if (cache && cache !== '' && cacheTime && now - cacheTime < 60 * 60 * 1000) {
    let disciplines: DisciplinesResponse;
    disciplines = JSON.parse(cache);
    return disciplines;
  } else {
    let result = await getDisciplines();
    localStorage.setItem('cachedDisciplines', JSON.stringify(result));
    localStorage.setItem('cachedDisciplinesTime', now.toString());
    return result;
  }
}

/**
 * Popup interface used for selecting a discipline for the logged in user
 * @param param0 
 * @returns 
 */
const DisciplineModal = ({
  open = false,
  toggle,
  setDiscipline,
  disciplineId = 1,
  noInitialChange = false,
  repressAlert = false
}: Props) => {
  const user = useAppSelector((s) => s.user);
  const dispatch = useDispatch();
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [tempDisciplineId, setTempDisciplineId] = useState<number>(user.currentDiscipline?.disciplineId ?? user.currentDisciplineId ?? disciplineId);

  //get the disciplines 
  useAsync({
    promiseFn: getDisciplinesCached,
    onResolve: (r) => {
      if (r.data?.disciplines.length <= 0 && !repressAlert) {
        //TODO need to find and use a generatic message dialog
        alert("User doesn't have any disciplines.");
        return;
      }

      const disciplines = sortAlphabetically(r.data?.disciplines, 'name');
      setDisciplines(disciplines);
      const discipline = user.currentDiscipline ? user.currentDiscipline : (disciplineId === -1 ? disciplines[0] : disciplines.filter(d => d.disciplineId === disciplineId)[0]);
      setDiscipline(discipline);
      setTempDisciplineId(discipline.disciplineId ?? disciplineId);
    }
  });

  const handleSave = () => {
    let discipline = disciplines.find(d => d.disciplineId === (tempDisciplineId === -1 ? disciplineId : tempDisciplineId));
    dispatch(updateDiscipline(discipline!));
    dispatch(updateDisciplineId({ disciplineId: discipline?.disciplineId! }));
    setDiscipline(discipline!);

    //Save to local storage for persistence when first loading app
    localStorage.setItem('discipline', JSON.stringify(discipline));
    localStorage.setItem('disciplineId', disciplineId.toString());

    toggle(false);
  }
  return (
    <Modal
      saveText='Set Discipline'
      open={open}
      onCancel={() => toggle(false)}
      onSave={handleSave}
    >
      <Fragment>
        <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
          <Select
            onChange={(selected) => selected && setTempDisciplineId(Number(selected.value))}
            className='block mt-1'
            value={disciplines
              .map(t => ({ value: t.disciplineId, label: t.name }))
              .find(o => o.value === tempDisciplineId) || null}
            options={disciplines
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((t) => ({ value: t.disciplineId, label: t.name }))}
            styles={graySelectStyles}
          />
        </div>
      </Fragment>
    </Modal>
  )
}

export default DisciplineModal;